import { inject, Injectable, signal } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SortDirection } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import axios, { AxiosError } from 'axios';
import { format, isValid, parseISO } from 'date-fns';
import { BehaviorSubject, debounceTime, distinctUntilChanged, map, merge, skipUntil, startWith, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { z } from 'zod';
import { WorkflowService } from '../services/workflow/workflow.service';
import i18n from './translate';
import { ClientOption, GetCasesResponse, products, Stage, stages, TaskRecipient, taskRecipients, UNKNOWN_CLIENT_NAME } from './types';

@Injectable({
  providedIn: 'root',
})
export class PayeeCasesService {
  private route = inject(ActivatedRoute);
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private workflowService = inject(WorkflowService);

  private readonly destroy = new Subject<void>();
  readonly setCasesSubject = new Subject<void>();

  readonly getCasesPath = new BehaviorSubject<string | null>(null);
  readonly getDebtorsPath = new BehaviorSubject<string | null>(null);

  // Filters
  readonly client = new BehaviorSubject<ClientOption | null>(null);
  readonly stage = new BehaviorSubject<Stage | null>(null);
  readonly state = new BehaviorSubject<string | null>(null);
  readonly payee_case_reference_id = new BehaviorSubject<string | null>(null);
  readonly debtor_name = new BehaviorSubject<string | null>(null);
  readonly bulk_upload_name = new BehaviorSubject<string | null>(null);
  readonly show_deleted = new BehaviorSubject<1 | null>(null);
  readonly refund_missing = new BehaviorSubject<1 | null>(null);
  readonly law_firm_missing = new BehaviorSubject<1 | 0 | null>(null);
  readonly law_firm_name = new BehaviorSubject<string | null>(null);
  readonly started_at_min = new BehaviorSubject<string | Date | null>(null);
  readonly started_at_max = new BehaviorSubject<string | Date | null>(null);
  readonly transition_at_min = new BehaviorSubject<string | Date | null>(null);
  readonly transition_at_max = new BehaviorSubject<string | Date | null>(null);
  readonly product = new BehaviorSubject<string | null>(null);
  readonly has_task = new BehaviorSubject<1 | null>(null);
  readonly task_recipient = new BehaviorSubject<TaskRecipient | null>(null);

  // Sorts
  readonly sort_by = new BehaviorSubject<string | null>(null);
  readonly sort_direction = new BehaviorSubject<SortDirection>('');

  // Pagination
  readonly perPage = new BehaviorSubject<number>(10);
  readonly page = new BehaviorSubject<number>(1);

  ignoredFilters: Record<string, BehaviorSubject<any>> = {
    page: this.page,
    perPage: this.perPage,
    has_task: this.has_task,
  };

  readonly pagination = merge(
    this.page,
    this.perPage,
    this.sort_by,
    this.sort_direction,
  );

  readonly filters = merge(
    this.client,
    this.stage,
    this.state,
    this.payee_case_reference_id,
    this.debtor_name,
    this.bulk_upload_name,
    this.show_deleted,
    this.refund_missing,
    this.law_firm_missing,
    this.law_firm_name,
    this.started_at_min,
    this.started_at_max,
    this.transition_at_min,
    this.transition_at_max,
    this.product,
    this.has_task,
    this.task_recipient,
  );

  readonly filtersWithPagination = merge(
    this.pagination,
    this.filters,
  );

  readonly cases = new Subject<GetCasesResponse>();
  readonly loading = signal(false);
  readonly exporting = signal(false);

  private casesFetchGeneration = 0;

  constructor() {
    for (const [lang, translation] of Object.entries(i18n)) {
      this.translate.setTranslation(lang, translation, true);
    }
  }

  onInit(): void {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy))
      .subscribe(async () => {
        if (this.state.value !== null) {
          this.state.next(null);
        } else {
          await this.setCases();
        }
      });

    this.filtersWithPagination
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.setCasesSubject.next());

    this.workflowService.afterWorkflow
      .pipe(takeUntil(this.destroy))
      .subscribe(async data => {
        const id = data.payee_case.payee_case_reference_id;
        if (this.payee_case_reference_id.value === id) {
          await this.setCases();
        } else {
          this.payee_case_reference_id.next(id);
        }
      });

    const params = this.route.snapshot.queryParams;
    const stage = params.stage;
    if (stages.includes(stage)) {
      this.stage.next(stage);
    }

    const product = params.product;
    if (products.includes(product)) {
      this.product.next(product);
    }

    const state = params.state;
    if (state) {
      this.state.next(state);
    }

    const clientId = params.client_id;
    if (z.string().uuid().safeParse(clientId).success) {
      this.client.next({
        uuid: clientId,
        name: UNKNOWN_CLIENT_NAME,
      });
    }

    this.payee_case_reference_id.next(params.payee_case_reference_id || null);
    this.debtor_name.next(params.debtor_name || null);

    const law_firm_name = params.law_firm_name;
    if (law_firm_name) {
      this.law_firm_name.next(law_firm_name);
    }

    const show_deleted = params.show_deleted;
    if (show_deleted == 1) {
      this.show_deleted.next(show_deleted);
    }

    const refund_missing = params.refund_missing;
    if (refund_missing == 1) {
      this.refund_missing.next(refund_missing);
    }

    const law_firm_missing = params.law_firm_missing;
    if (law_firm_missing == 0 || law_firm_missing == 1) {
      this.law_firm_missing.next(law_firm_missing);
    }

    const startedAtMin = params.startedAtMin;
    if (startedAtMin && isValid(parseISO(startedAtMin))) {
      this.started_at_min.next(startedAtMin);
    }

    const startedAtMax = params.startedAtMax;
    if (startedAtMax && isValid(parseISO(startedAtMax))) {
      this.started_at_max.next(startedAtMax);
    }

    const transitionAtMin = params.transitionAtMin;
    if (transitionAtMin && isValid(parseISO(transitionAtMin))) {
      this.transition_at_min.next(transitionAtMin);
    }

    const transitionAtMax = params.transitionAtMax;
    if (transitionAtMax && isValid(parseISO(transitionAtMax))) {
      this.transition_at_max.next(transitionAtMax);
    }

    const bulk_upload_name = this.route.snapshot.queryParams.bulk_upload_name;
    if (bulk_upload_name) {
      this.bulk_upload_name.next(bulk_upload_name);
    }

    const has_task = params.has_task;
    if (has_task == 1) {
      this.has_task.next(1);

      const task_recipient = params.task_recipient;
      if (taskRecipients.includes(task_recipient)) {
        this.task_recipient.next(task_recipient);
      }
    }

    const sort_by = params.sort_by;
    if (sort_by) {
      this.sort_by.next(sort_by);
    }

    if (sort_by) {
      let sort_direction = params.sort_direction;
      if (!(['asc', 'desc'].includes(sort_direction))) {
        sort_direction = 'asc';
      }
      this.sort_direction.next(sort_direction);
    }

    const page = parseInt(params.page);
    if (!isNaN(page)) {
      this.page.next(Math.max(page, 1));
    }

    const perPage = parseInt(params.perPage);
    if (!isNaN(perPage)) {
      this.perPage.next(Math.max(perPage, 1));
    }

    this.filters
      .pipe(
        skipUntil(this.cases),
        takeUntil(this.destroy),
      )
      .subscribe(() => {
        if (this.page.value !== 1) {
          this.page.next(1);
        }
      });

    this.loading.set(true);

    this.setCasesSubject
      .pipe(
        takeUntil(this.destroy),
        startWith(this.getFilters()),
        debounceTime(200),
        map(() => JSON.stringify(this.getFilters())),
        distinctUntilChanged(),
      )
      .subscribe(async () => {
        await this.setCases();
      });
  }

  onDestroy(): void {
    this.destroy.next();
  }

  setPagination(event: PageEvent): PayeeCasesService {
    if (this.perPage.value === event.pageSize) {
      this.page.next(event.pageIndex + 1);
    } else {
      this.page.next(1);
    }
    this.perPage.next(event.pageSize);

    return this;
  }

  resetFilters(): PayeeCasesService {
    this.client.next(null);
    this.stage.next(null);
    this.state.next(null);
    this.payee_case_reference_id.next(null);
    this.debtor_name.next(null);
    this.bulk_upload_name.next(null);
    this.show_deleted.next(null);
    this.refund_missing.next(null);
    this.law_firm_missing.next(null);
    this.law_firm_name.next(null);
    this.started_at_min.next(null);
    this.started_at_max.next(null);
    this.transition_at_min.next(null);
    this.transition_at_max.next(null);
    this.product.next(null);
    this.has_task.next(null);
    this.task_recipient.next(null);
    this.perPage.next(10);
    this.page.next(1);

    return this;
  }

  getFilters(): Record<string, string | number | null> {
    let startedAtMin = this.started_at_min.value;
    if (startedAtMin instanceof Date) {
      startedAtMin = format(startedAtMin, 'yyyy-MM-dd');
    }

    let startedAtMax = this.started_at_max.value;
    if (startedAtMax instanceof Date) {
      startedAtMax = format(startedAtMax, 'yyyy-MM-dd');
    }

    let transitionAtMin = this.transition_at_min.value;
    if (transitionAtMin instanceof Date) {
      transitionAtMin = format(transitionAtMin, 'yyyy-MM-dd');
    }

    let transitionAtMax = this.transition_at_max.value;
    if (transitionAtMax instanceof Date) {
      transitionAtMax = format(transitionAtMax, 'yyyy-MM-dd');
    }

    return {
      stage: this.stage.value,
      state: this.state.value,
      payee_case_reference_id: this.payee_case_reference_id.value,
      debtor_name: this.debtor_name.value,
      bulk_upload_name: this.bulk_upload_name.value || null,
      show_deleted: this.show_deleted.value,
      refund_missing: this.refund_missing.value,
      law_firm_missing: this.law_firm_missing.value,
      law_firm_name: this.law_firm_name.value,
      startedAtMin,
      startedAtMax,
      transitionAtMin,
      transitionAtMax,
      product: this.product.value,
      has_task: this.has_task.value,
      task_recipient: this.has_task.value === 1 ? this.task_recipient.value : null,
      page: this.page.value,
      sort_by: this.sort_direction.value ? this.sort_by.value : null,
      sort_direction: this.sort_direction.value || null,
      perPage: this.perPage.value,
      client_id: this.client.value?.uuid ?? null,
    };
  }

  async exportCases(): Promise<void> {
    if (!this.getCasesPath.value || this.exporting()) {
      return;
    }

    try {
      this.exporting.set(true);

      const result = await axios.get<Blob>(
        `${environment.baseUrl}/${this.getCasesPath.value}/export`,
        {
          responseType: 'blob',
          params: this.getFilters(),
        },
      );

      const link = document.createElement('a');
      link.href = URL.createObjectURL(result.data);
      link.download = `${this.translate.instant('CASES_TABLE.EXPORT.FILENAME')}-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Network error while exporting cases', error.response?.data);
      } else {
        console.error('Unknown error while exporting cases', error);
      }

      this.snackbar.open(
        this.translate.instant('CASES_TABLE.ERROR_MESSAGES.ERROR_WHILE_EXPORTING_CASES'),
        'OK',
        {
          duration: 5000,
        },
      );
    } finally {
      this.exporting.set(false);
    }
  }

  async setCases(): Promise<void> {
    if (!this.getCasesPath.value) {
      console.warn('Get cases path is not set!');
      return;
    }

    const generation = ++this.casesFetchGeneration;

    try {
      this.loading.set(true);

      const result = await axios.get<GetCasesResponse>(
        `${environment.baseUrl}/${this.getCasesPath.value}`,
        {
          params: this.getFilters(),
        }
      );

      if (generation !== this.casesFetchGeneration) {
        return;
      }

      this.cases.next(result.data);

      if (
        result.data.data.length > 0
        && this.stage.value
        && result.data.data.every(v => v.state.stage !== this.stage.value)
      ) {
        this.stage.next(result.data.data[0].state.stage);
      }
    } catch (error: AxiosError | unknown) {
      if (generation !== this.casesFetchGeneration) {
        return;
      }

      if (error instanceof AxiosError) {
        console.error('Network error while getting cases table', error.response?.data);
      } else {
        console.error('Unknown error while getting cases table', error);
      }

      this.snackbar.open(
        this.translate.instant('CASES_TABLE.ERROR_MESSAGES.ERROR_WHILE_LOADING_CASES'),
        'OK',
        {
          duration: 5000,
        }
      );
    } finally {
      if (generation === this.casesFetchGeneration) {
        this.loading.set(false);
      }
    }
  }
}
